import React from 'react';
import Header from './Header';
import background from './img/softwarePartners.png';
import MobileNavigation from './MobileNavigation';
import { CopyBlock, dracula } from 'react-code-blocks';
import Footer from './Footer';
import Step1 from './img/Step1Emulator.webp';
import Step2 from './img/Step2Emulator.webp';
import Step4a from './img/Step4aEmulator.webp';
import Step4b from './img/Step4bEmulator.webp';

const DeveloperSandbox = () => {
  return (
    <>
      <MobileNavigation />
      <section className="bg-[#212121]">
        <div
          className="w-full pb-28"
          style={{
            backgroundImage: `url(${background})`,
            backgroundSize: '200% 200%',
            backgroundPosition: 'bottom center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <Header />
          <div className="p-4 row md:p-8 how">
            <h3 className="text-white large">Sandbox Emulator Integration Guide</h3>
          </div>
          <div className="flex p-16 row flex-col justify-center flex-start gap-6 self-stretch rounded-[28px] bg-white !mb-10">
            <h2 className="text-black large">Overview</h2>
            <p className="text-black light">
              Basys Physical Terminals support secure, in-person transactions. To streamline integration and testing, the Sandbox Emulator simulates real transaction flows—no physical terminals or test cards required. This guide explains how to integrate using Basys REST APIs in conjunction with the Sandbox Terminal Emulator associated to your gateway.
            </p>
            <h2 className="font-normal text-black">Prerequisites</h2>
            <p className="text-black light">Before proceeding with this guide, ensure you have:</p>
            <ul className="ml-5 list-disc">
              <li className="text-black light">An active Basys sandbox environment account</li>
              <li className="text-black light">Access to IQPro+</li>
              <li className="text-black light">
                Although not required, we recommend using{' '}
                <a
                  href="https://www.postman.com/downloads/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-500 underline"
                >
                  Postman
                </a>{' '}
                to become familiar with our APIs
              </li>
              <li className="text-black light">
                Read our{' '}
                <a
                  href="https://sandbox.api.basyspro.com/iqsaas/v1/scalar/#description/authentication"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-500 underline"
                >
                  Authorization
                </a>{' '}
                page prior to this proceeding
              </li>
            </ul>
            <p className="text-black light">
              If you need assistance with onboarding or setting up any prerequisites, please contact
              Basys Integration Support:
            </p>
            <ul className="ml-5 list-disc">
              <li className="text-black light">Phone: (800) 851-9460</li>
              <li className="text-black light">
                Email:{' '}
                <a
                  href="mailto:integrationsupport@basyspro.com"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-500 underline"
                >
                  integrationsupport@basyspro.com
                </a>
              </li>
            </ul>
            <h2 className="text-black large">Create/Launch Emulator</h2>
            <ol className="ml-5 list-decimal">
              <li className="text-black light">
                From the Physical Terminals View, select 'Create New' to create a Sandbox Terminal.
                <ol className='ml-5 list-none'>
                <li className="text-black light"><img src={Step1} alt="iQ Pro+ Physical Terminals view" /></li>
                </ol>
              </li>
              <li className="text-black light">The 'Add New Terminal' popup displays (first time only) and the Terminal Name is pre-populated as “Terminal Emulator” and the Processor Name is either pre-populated or selectable from the PT processor list.
                <ol className='ml-5 list-none'>
                <li className="text-black light"><img src={Step2} alt="create new terminal dialog" /></li>
                </ol>
              </li>
              <li className="text-black light">Once created, the terminal is added to the gateway (first time only).</li>
              <li className="text-black light">A popup will display the URL to launch the emulator, along with associated connection information. Please Note: For security reasons this is the only opportunity to copy or record your secret. It will not be displayed again and cannot be retrieved at a later time.	 
                <ol className='ml-5 list-[lower-alpha]'>
                  <li className="text-black light">First Creation:
                    <ol className='ml-5 list-none'>
                      <li className="text-black light"><img src={Step4a} alt="Terminal Emulator Info Popup" /></li>
                      </ol>
                  </li>
                  <li className="text-black light">Subsequent Creations:
                    <ol className='ml-5 list-none'>
                      <li className="text-black light"><img src={Step4b} alt="Terminal Emulator Info Access" /></li>
                    </ol>
                  </li>
                </ol>
              </li>
              <li className="text-black light">Launch the emulator — it will open in a new window.</li>
            </ol>
            <h2 className="text-black large">Authentication</h2>
            <p className="text-black light">
              All API requests require OAuth 2.0 authentication using the client credentials flow.
            </p>
            <h2 className="font-normal text-black">Obtaining an Access Token</h2>
            <CopyBlock
              text="POST <https://sandbox.account.basyspro.com/sandbox.account.basyspro.com/B2C_1A_IQPROV2_INTEGRATOR/oauth2/v2.0/token>"
              showLineNumbers={false}
              theme={dracula}
              customStyle={{overflowX: 'auto'}}
            />
            <h2 className="font-normal text-black">Request Body Properties:</h2>
            <ul className="ml-5 list-disc">
              <li className="text-black light">
                <code>grant_type</code>: client_credentials
              </li>
              <li className="text-black light">
                <code>client_id</code>: Your client ID
              </li>
              <li className="text-black light">
                <code>client_secret</code>: Your client secret
              </li>
              <li className="text-black light">
                <code>scope</code>: https://sandbox.account.basyspro.com/c7d093fb-b130-44a9-a733-529551341975/.default
              </li>
              <li className="text-black light">
                <code>TerminalID</code>: Terminal ID for the emulated device
              </li>
              <li className="text-black light">
                Please Note: When requesting a JWT token from the authentication endpoint, you must submit the request using the x-www-form-urlencoded content type. This format is required for the server to properly parse the request parameters. 
                </li>
            </ul>
            <h2 className="font-normal text-black">Sample Output:</h2>
            <CopyBlock
              text={`{ 
  "access_token": "<jwt_token_string>", 
  "token_type": "Bearer", 
  "not_before": 1754417945, 
  "expires_in": 10080, 
  "expires_on": 1754428025, 
  "resource": "<resource_id>" 
} `}
              language="json"
              theme={dracula}
            />
            <p className="text-black light">
              Next:
            </p>
            <ul className="ml-5 list-disc">
              <li className="text-black light">
                Store this access token securely — you'll need it for all subsequent API calls —
                but it will expire after two hours so be prepared to retrieve a new access token as
                needed.
              </li>
              <li className="text-black light">
                Paste the JWT token into the 'Physical Terminal JWT' field in the emulator
              </li>
            </ul>
            <h2 className="text-black large">Core Integration Flow</h2>
            <h2 className="font-normal text-black">1. Retrieve Gateway Information</h2>
            <p className="text-black light">
              First, get your merchant’s gateway ID which is required for terminal operations.
            </p>
            <CopyBlock
              text="GET <https://sandbox.api.basyspro.com/iqsaas/v1/api/gateway>
Authorization: Bearer {access_token}"
              theme={dracula}
              customStyle={{overflowX: 'auto'}}
            />
            <p className="text-black light">
              Next: Store the gatewayId, provided in the response for future requests. This
              represents the merchant/business.
            </p>
            <h2 className="font-normal text-black">2. List Available Terminals</h2>
            <p className="text-black light">
              Using the gatewayId, retrieve all terminals associated with your gateway:
            </p>
            <CopyBlock
              text="GET <https://sandbox.api.basyspro.com/iqsaas/v1/api/gateway/{gatewayId}/terminal?offset=1&limit=100>
Authorization: Bearer {access_token}"
              theme={dracula}
              customStyle={{overflowX: 'auto'}}
            />
            <p className="text-black light">
              This endpoint returns details about your terminals including:
            </p>
            <ul className="ml-5 list-disc">
              <li className="text-black light">Terminal ID</li>
              <li className="text-black light">Serial number</li>
              <li className="text-black light">Model</li>
              <li className="text-black light">Current status</li>
            </ul>
            <p className="text-black light">
              Next: Store the <code>terminalId</code> provided in the response to specify which
              terminal to accept the payment on.
            </p>
            ...
            <h2 className="font-normal text-black">3. Initiate a Payment</h2>
            <p className="text-black light">
              To process a payment, create a payment intent by specifying the gatewayId and
              terminalId as URL parameters:
            </p>
            <CopyBlock
              text={`POST <https://sandbox.api.basyspro.com/iqsaas/v1/api/gateway/{gatewayId}/terminal/{terminalId}/command/payment-intent>
Authorization: Bearer {access_token}
Body:
{
    "tenderType": ["Unknown"],
    "transactionType": "Sale",
    "remit": {
        "baseAmount": 0,
        "taxAmount": 0,
        "taxExempt": true,
        "currencyCode": "USD",
        "total": 0.25
    }
}`}
              theme={dracula}
              customStyle={{overflowX: 'auto'}}
            />
            <p className="text-black light">Key fields:</p>
            <ul className="ml-5 list-disc">
              <li className="text-black light">
                <code>tenderType</code>: Payment method (“Unknown”, “Eft”, “EBTCash”, or “SnapFSA”)
              </li>
              <li className="text-black light">
                <code>transactionType</code>: Transaction type ("Sale", “Authorize” or “Credit”)
              </li>
              <li className="text-black light">
                <code>remit</code>: Payment details including:
              </li>
              <ul className="ml-5 list-disc">
                <li className="text-black light">
                  <code>baseAmount</code>: Base transaction amount before tax
                </li>
                <li className="text-black light">
                  <code>taxAmount</code>: Tax amount
                </li>
                <li className="text-black light">
                  <code>taxExempt</code>: Boolean indicating if transaction is tax exempt
                </li>
                <li className="text-black light">
                  <code>currencyCode</code>: Three-letter currency code (e.g., "USD")
                </li>
                <li className="text-black light">
                  <code>total</code>: Total transaction amount
                </li>
              </ul>
            </ul>
            <h2 className="text-black light">Payment Intent Response</h2>
            <p className="text-black light">
              Upon success, you will receive an asynchronous response containing the transactionId before the
              payment flow between the customer and device completes.
            </p>
            <p className="text-black light">Example response:</p>
                        <CopyBlock
                          text={`{ 
     "statusCode": "OK", 
     "data": { 
          "transactionId": "3fe5dc74-f3ac-4a46-a262-a4667fec4ad3", 
          "acceptedProcessTransaction": true 
     } 
}`}
                          language="json"
                          theme={dracula}
                        />
            <p className="text-black light">
              Next: Use the <code>transactionId</code> to retrieve the transaction status.
            </p>
            <h2 className="font-normal text-black">4. Retrieve the Transaction Status</h2>
                        <p className="text-black light">
                          After creating a payment intent, use the <code>transactionId</code> to check its
                          status and whether it was successful or not:
                        </p>
                        <CopyBlock
                          text={`GET <https://sandbox.api.basyspro.com/iqsaas/v1/api/gateway/{gatewayId}/terminal/{terminalId}/transaction/{transactionId}/command
                            /payment-status>
            Authorization: Bearer {access_token}`}
                          theme={dracula}
                          customStyle={{ overflowX: 'auto' }}
                          
                        />
                        <p className="text-black light">
                          The response includes an HTTP status code and additional structured transaction
                          details.
                        </p>
                        <p className="text-black light">
                          The stopListening flag in the response explicitly tells you whether to continue
                          polling:
                        </p>
                        <ul className="ml-5 list-disc">
                          <li className="text-black light">
                            true: No further polling is needed (e.g., for statuses 200 OK, 410 Gone, 404 Not
                            Found, 403 Forbidden).
                          </li>
                          <li className="text-black light">
                            false: Transaction is still processing; continue polling.
                          </li>
                        </ul>
                        <p className="text-black light">Response Structure:</p>
                        <CopyBlock
                          text={`{ 
              "statusDetails": ["<Status Description>"], 
              "statusCode": <HTTP Status Code>, 
              "data": { 
                "stopListening": <true | false>, 
                "transactionProcessStatus": [<Array in descending order of occurrence for all available statuses>], 
                "transactionDetails": <Optional - transactionDetails if applicable> 
              } 
            }`}
                          theme={dracula}
                          customStyle={{ overflowX: 'auto' }}
                          language='json'
                        />
                        <h2 className="text-black light">Response scenarios</h2>
                        <h3 className="font-semibold text-black light">HTTP 410 Gone</h3>
                        <p className="text-black light">
                          Returned when the transaction status is any of the following:{' '}
                        </p>
                        <ul className="ml-5 list-disc">
                          <li className="text-black light">ErroredOut (-100) </li>
                          <li className="text-black light">Rejected (200)</li>
                          <li className="text-black light">CustomerCanceled (400)</li>
                          <li className="text-black light">MerchantCanceled (1200)</li>
                          <li className="text-black light">Declined (1100)</li>
                        </ul>
                        <p className="text-black light">Example response:</p>
                        <CopyBlock
                          text={`{ 
              "statusDetails": ["Transaction errored out"], 
              "statusCode": 410, 
              "data": { 
                "stopListening": true, 
                "transactionProcessStatus": [-100, ..., 100] 
              } 
            } `}
                          theme={dracula}
                          customStyle={{ overflowX: 'auto' }}
                          language='json'
                        />
                        <h3 className="font-semibold text-black light">HTTP 200 OK</h3>
                        <p className="text-black light">
                          Returned when the transaction status is any of the following:
                        </p>
                        <ul className="ml-5 list-disc">
                          <li className="text-black light">Approved (1000)</li>
                          <li className="text-black light">Declined (1100)</li>
                        </ul>
                        <p className="text-black light">Example response:</p>
                        <CopyBlock
                          text={`{ 
              "statusDetails": ["Approved"], 
              "statusCode": 200, 
              "data": { 
                "stopListening": true, 
                "transactionProcessStatus": [1000, ..., 100], 
                "transactionDetails": {/* transactionDetails */} 
              } 
            } `}
                          theme={dracula}
                          customStyle={{ overflowX: 'auto' }}
                          language='json'
                        />
                        <h3 className="font-semibold text-black light">HTTP 202 Accepted</h3>
                        <p className="text-black light">
                          Returned for any other transaction status not explicitly listed above.
                        </p>
                        <p className="text-black light">Example response:</p>
                        <CopyBlock
                          text={`{ 
              "statusDetails": ["Accepted"], 
              "statusCode": 202, 
              "data": { 
                "stopListening": false, 
                "transactionProcessStatus": [..., 500, 300, 100] 
              } 
            } `}
                          theme={dracula}
                          customStyle={{ overflowX: 'auto' }}
                          language='json'
                        />
                        <h3 className="font-semibold text-black light">HTTP 301 Moved Permanently</h3>
                        <p className="text-black light">
                          Response is returned for a PIN decline with retry, providing a new transaction ID for
                          continued tracking. The status of the original and any new transaction will be included in the response.
                        </p>
                        <p className="text-black light">Example response:</p>
                        <CopyBlock
                          text={`{ 
              "statusDetails": ["Moved Permanently"], 
              "statusCode": 301, 
              "data": { 
                "stopListening": true, 
                "transactionProcessStatus": [..., 500, 300, 100] 
                "transactionid": “New Transaction ID” 
              } 
            } `}
                          theme={dracula}
                          customStyle={{ overflowX: 'auto' }}
                          language='json'
                        />
                        <p className="text-black light">Please Note: To return the details for any new transaction from a pin retry please utilize the Transaction Details endpoint. </p>
                        <CopyBlock
                          text={`GET <https://sandbox.api.basyspro.com/iqsaas/v1/api/gateway/{gatewayId}/terminal/{terminalId}/transaction/{transactionId}>
            Authorization: Bearer {access_token}`}
                          theme={dracula}
                          customStyle={{ overflowX: 'auto' }}
                          
                        />
            
                        <p className="text-black light">
                          It is our recommended strategy to leverage our transaction webhook functionality to
                          listen for transaction activity. However, if you are unable to support an endpoint to
                          consume webhooks you can implement a polling strategy mentioned in the Best Practices
                          -{'>'}{' '}
                          <a href="#transaction-timing" className="text-blue-500 underline">
                            Transaction Timing
                          </a>{' '}
                          section below.
                        </p>
            <h2 className="font-normal text-black">5. Void or Reverse a Transaction</h2>
            <p className="text-black light">To void a transaction:</p>
            <CopyBlock
              text={`PUT <https://sandbox.api.basyspro.com/iqsaas/v1/api/gateway/{gatewayId}/transaction/{transactionId}/void>
Authorization: Bearer {access_token}`}
              theme={dracula}
              customStyle={{overflowX: 'auto'}}
            />
            <h2 className="text-black light">Accept or Decline Transaction from Emulator</h2>
            <p className="text-black light">
              Within the emulator window, the Integration User can simulate cardholder interaction by selecting either 'Accept' or 'Decline' on the emulated terminal. This triggers a corresponding approval or rejection for the test transaction within the sandbox environment. 
            </p>
            <h2 className="text-black large">Best Practices</h2>
            <div>
              <h3 className="font-semibold text-black light">Error Handling</h3>
              <ul className="ml-5 list-disc">
                <li className="text-black light">Always check HTTP status codes</li>
                <li className="text-black light">
                  Implement proper retry logic for network failures
                </li>
                <li className="text-black light">Store transaction IDs for reconciliation</li>
              </ul>

              <h3 className="font-semibold text-black light">Security</h3>
              <ul className="ml-5 list-disc">
                <li className="text-black light">Never expose your client credentials</li>
                <li className="text-black light">
                  Implement client-side encryption for sensitive data
                </li>
              </ul>

              <h3 className="font-semibold text-black">Transaction Management</h3>
              <ul className="ml-5 list-disc">
                <li className="text-black light">Keep transaction logs for reconciliation</li>
              </ul>

              <h3 className="font-semibold text-black light" id="transaction-timing">
                Transaction Timing
              </h3>
              <p className="text-black light">
                The payment intent endpoint returns a <code>transactionId</code> asynchronously. We
                recommend implementing a polling strategy with exponential backoff, starting at
                2-second intervals, for up to 180 seconds (3 minutes) to ensure reliable capture of
                the transaction result. Most transactions complete significantly faster, but this
                maximum window accounts for edge cases and potential processing delays.
              </p>
              <p className="text-black light">
                Our system has a maximum total processing window of 169 seconds, which accounts for
                the following maximum timeout values across our processing chain:
              </p>
              <ul className="ml-5 list-disc">
                <li className="text-black light">Initial terminal communication: 10 seconds</li>
                <li className="text-black light">Terminal inactivity timeout: 60 seconds</li>
                <li className="text-black light">Tokenization processing: 59 seconds</li>
                <li className="text-black light">Payment processing: 40 seconds</li>
              </ul>
            </div>
            <h2 className="text-black large">Support</h2>
            <p className="text-black light">For additional support:</p>
            <ul className="ml-5 list-disc">
              <li className="text-black light">
                Review our{' '}
                <a
                  href="https://sandbox.api.basyspro.com/iqsaas/v1/scalar"
                  className="text-blue-500 underline"
                >
                  API Documentation
                </a>
              </li>
              <li className="text-black light">
                Contact support at{' '}
                <a href="mailto:support@basyspro.com" className="text-blue-500 underline">
                  support@basyspro.com
                </a>
              </li>
            </ul>
            <h2 className="text-black large">Next Steps</h2>
            <p className="text-black light">After implementing the basic integration:</p>
            <ol className="ml-5 list-decimal">
              <li className="text-black light">
                Add support for additional transaction types that your merchants may require.
              </li>
              <li className="text-black light">
                Make sure to handle errors and edge cases and capture logs within your
                implementation.
              </li>
              <li className="text-black light">Test your implementation thoroughly.</li>
            </ol>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default DeveloperSandbox;
